'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * /services/revenue-first-design -- the body of the page, ported from
 * `public/radar-branding/revenue-first-design.html`. See
 * `revenue-first-design.css` for what the port changed and why.
 *
 * Client only for the two behaviours the mock's own script carried:
 *
 *   1. Reveal on scroll. Content renders VISIBLE by default and is only armed
 *      once this component mounts (`is-enhanced`), so SSR, a failed hydration
 *      and `prefers-reduced-motion` all show the finished page rather than a
 *      blank one. The mock hid `.reveal` unconditionally, which renders an
 *      empty document if its script never runs.
 *   2. The FAQ show-more toggle.
 *
 * The mock's third behaviour, the scroll-progress bar, is dropped: the root
 * layout already renders `ScrollProgress`.
 */
export interface FaqItem {
  question: string
  answer: string
}

/**
 * `heroPrice` comes from `getServicePriceModel()` in the route -- the same call
 * that builds the JSON-LD Offer. This string used to be hardcoded here, which
 * is exactly the drift that helper exists to prevent: the visible price and the
 * structured-data price have to move together.
 */
export default function RevenueFirstDesignContent({
  faqItems,
  heroPrice,
}: {
  faqItems: FaqItem[]
  heroPrice?: string | null
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [faqOpen, setFaqOpen] = useState(false)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const targets = Array.from(root.querySelectorAll('.rfd-reveal'))
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(el => el.classList.add('rfd-in'))
      return
    }
    setEnhanced(true)
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('rfd-in')
            io.unobserve(e.target)
          }
        }),
      { threshold: 0.08, rootMargin: '0px 0px -35px' }
    )
    targets.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={rootRef}
      className={`rfd-page${enhanced ? ' rfd-is-enhanced' : ''}`}
    >
      <section className='rfd-hero' aria-labelledby='hero-title'>
        <div className='rfd-wrap'>
          <div className='rfd-hero-grid'>
            <div className='rfd-hero-copy rfd-reveal rfd-in'>
              <p className='rfd-label rfd-hero-eyebrow'>
                Best for Series A+ SaaS with an engineer-designed brand
              </p>
              <h1 id='hero-title' className='rfd-display'>
                We design brands that{' '}
                <span className='rfd-pink'>close deals,</span> not just brands
                that look good.
              </h1>
              <p className='rfd-hero-body'>
                We build your logo, design system, component library, pitch
                decks, and sales materials so your product stops looking like a
                hackathon project. Not for teams without a product yet (build
                something first) or those who just need a logo ($1,995 tier
                covers that).
              </p>
              <p className='rfd-hero-support'>
                Brand identity, design systems, and conversion assets shipped as
                one cohesive system.
              </p>
              <div className='rfd-hero-actions'>
                <a className='rfd-btn rfd-btn-yellow' href='/contact-us'>
                  Plan your system{' '}
                  <span className='rfd-arrow' aria-hidden>
                    ↗
                  </span>
                </a>
              </div>
              {heroPrice ? <p className='rfd-hero-price'>{heroPrice}</p> : null}
            </div>

            <div
              className='rfd-sync-console rfd-reveal rfd-in'
              aria-label='Brand system synchronisation monitor'
            >
              <div className='rfd-console-head'>
                <p className='rfd-label rfd-label-blue'>System sync / live</p>
                <span className='rfd-console-status'>
                  <i aria-hidden></i> 4 surfaces aligned
                </span>
              </div>
              <div className='rfd-sync-frame'>
                <svg
                  viewBox='0 0 240 240'
                  role='img'
                  aria-label='Animated diagram of one brand system syncing across product, site, deck, and email surfaces'
                >
                  <g
                    style={{ stroke: 'var(--r-diagram-ring)' }}
                    strokeWidth='1'
                  >
                    <line x1='120' y1='120' x2='120' y2='60' />
                    <line x1='120' y1='120' x2='176' y2='120' />
                    <line x1='120' y1='120' x2='120' y2='180' />
                    <line x1='120' y1='120' x2='64' y2='120' />
                  </g>
                  <circle
                    cx='120'
                    cy='120'
                    r='18'
                    fill='none'
                    strokeWidth='1.5'
                    className='rfd-pm-brand-sync'
                    style={{ stroke: 'var(--r-accent-blue-text)' }}
                  />
                  <circle
                    cx='120'
                    cy='120'
                    r='18'
                    fill='none'
                    strokeWidth='1.5'
                    className='rfd-pm-brand-sync'
                    style={{
                      stroke: 'var(--r-accent-blue-text)',
                      animationDelay: '1.3s',
                    }}
                  />
                  <g className='rfd-pm-brand-pulse'>
                    <rect
                      x='104'
                      y='36'
                      width='32'
                      height='24'
                      strokeWidth='1'
                      style={{
                        fill: 'color-mix(in srgb,var(--r-accent-blue-text) 12%,transparent)',
                        stroke: 'var(--r-accent-blue-text)',
                      }}
                    />
                    <rect
                      x='109'
                      y='43'
                      width='22'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                    <rect
                      x='109'
                      y='50'
                      width='14'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                  </g>
                  <g className='rfd-pm-brand-pulse'>
                    <rect
                      x='176'
                      y='108'
                      width='32'
                      height='24'
                      strokeWidth='1'
                      style={{
                        fill: 'color-mix(in srgb,var(--r-accent-blue-text) 12%,transparent)',
                        stroke: 'var(--r-accent-blue-text)',
                      }}
                    />
                    <rect
                      x='181'
                      y='115'
                      width='22'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                    <rect
                      x='181'
                      y='122'
                      width='14'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                  </g>
                  <g className='rfd-pm-brand-pulse'>
                    <rect
                      x='104'
                      y='180'
                      width='32'
                      height='24'
                      strokeWidth='1'
                      style={{
                        fill: 'color-mix(in srgb,var(--r-accent-blue-text) 12%,transparent)',
                        stroke: 'var(--r-accent-blue-text)',
                      }}
                    />
                    <rect
                      x='109'
                      y='187'
                      width='22'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                    <rect
                      x='109'
                      y='194'
                      width='14'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                  </g>
                  <g className='rfd-pm-brand-pulse'>
                    <rect
                      x='32'
                      y='108'
                      width='32'
                      height='24'
                      strokeWidth='1'
                      style={{
                        fill: 'color-mix(in srgb,var(--r-accent-blue-text) 12%,transparent)',
                        stroke: 'var(--r-accent-blue-text)',
                      }}
                    />
                    <rect
                      x='37'
                      y='115'
                      width='22'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                    <rect
                      x='37'
                      y='122'
                      width='14'
                      height='3'
                      style={{ fill: 'var(--r-diagram-ring)' }}
                    />
                  </g>
                  <rect
                    x='106'
                    y='106'
                    width='28'
                    height='28'
                    style={{ fill: 'var(--r-accent-blue-text)' }}
                  />
                  <rect
                    x='113'
                    y='113'
                    width='14'
                    height='14'
                    fill='var(--r-ink)'
                    fillOpacity='0.9'
                  />
                </svg>
              </div>
              <p className='rfd-sync-caption'>
                One brand system, synced across every surface your customers
                see.
              </p>
              <div className='rfd-sync-legend' aria-label='Synced surfaces'>
                <span>
                  <i aria-hidden></i>Product
                </span>
                <span>
                  <i aria-hidden></i>Site
                </span>
                <span>
                  <i aria-hidden></i>Deck
                </span>
                <span>
                  <i aria-hidden></i>Email
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='rfd-context-bar'>
        <div className='rfd-wrap'>
          <div
            className='rfd-brand-bus rfd-reveal'
            aria-label='Brand system path from source of truth to revenue'
          >
            <div className='rfd-bus-node'>
              <span>Source of truth</span>
            </div>
            <div className='rfd-bus-node'>
              <span>Product</span>
            </div>
            <div className='rfd-bus-node'>
              <span>Site</span>
            </div>
            <div className='rfd-bus-node'>
              <span>Deck</span>
            </div>
            <div className='rfd-bus-node'>
              <span>Email</span>
            </div>
            <div className='rfd-bus-node'>
              <span>Revenue</span>
            </div>
          </div>
        </div>
      </section>

      <section className='rfd-context' aria-labelledby='context-title'>
        <div className='rfd-wrap'>
          <div className='rfd-context-grid rfd-reveal'>
            <h2 id='context-title' className='rfd-display'>
              One company.
              <br />
              One visual language.
            </h2>
            <p className='rfd-context-copy'>
              If your product looks like it was designed by engineers at 2am,
              customers do not trust it enough to pay. We build your brand
              identity, design system, component library, pitch decks, and sales
              collateral as one cohesive system. Everything ships together so
              your product, your website, and your sales materials all look like
              they came from the same company.
            </p>
          </div>
          <div className='rfd-stats rfd-reveal'>
            <article className='rfd-stat'>
              <strong>45 days</strong>
              <p className='rfd-label'>System live in product</p>
              <p>
                Figma tokens synced to production components with governance in
                place.
              </p>
            </article>
            <article className='rfd-stat'>
              <strong>One system</strong>
              <p className='rfd-label'>Brand, product, sales</p>
              <p>
                Automated brand-kit updates eliminate handoffs between design
                and GTM teams.
              </p>
            </article>
            <article className='rfd-stat'>
              <strong>Figma → code</strong>
              <p className='rfd-label'>Tokens stay in sync</p>
              <p>
                Design tokens map straight to production components, so the
                system never drifts from the brand.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='rfd-diagnostic' aria-labelledby='diagnostic-title'>
        <div className='rfd-wrap'>
          <div className='rfd-section-head rfd-reveal'>
            <div>
              <p className='rfd-label rfd-label-pink'>
                Frankenbrand vs one system
              </p>
              <h2 id='diagnostic-title' className='rfd-display'>
                Buyers notice
                <br />
                the drift.
              </h2>
            </div>
            <p>
              When every surface looks different, buyers read risk. When they
              match, they read a company worth paying.
            </p>
          </div>
          <div className='rfd-diagnostic-board rfd-reveal'>
            <article className='rfd-diag-lane'>
              <div className='rfd-diag-title'>
                <h3>Frankenbrand</h3>
                <span>Status / drift</span>
              </div>
              <div className='rfd-misalign'>
                <div className='rfd-strip'>Logo says one thing</div>
                <div className='rfd-strip'>Deck says another</div>
                <div className='rfd-strip'>Site looks like a third</div>
              </div>
            </article>
            <article className='rfd-diag-lane'>
              <div className='rfd-diag-title'>
                <h3>One system</h3>
                <span>Status / aligned</span>
              </div>
              <div className='rfd-align'>
                <div className='rfd-strip'>
                  One logo, color, and type system
                </div>
                <div className='rfd-strip'>Every surface matches</div>
                <div className='rfd-strip'>Trust converts to revenue</div>
              </div>
            </article>
            <div className='rfd-diag-conclusion'>
              The gap: an inconsistent brand reads as a hackathon project. One
              system, shared across product, site, and sales, reads as a company
              customers trust enough to pay.
            </div>
          </div>
        </div>
      </section>

      <section
        className='rfd-pricing'
        id='pricing'
        aria-labelledby='pricing-title'
      >
        <div className='rfd-wrap'>
          <div className='rfd-section-head rfd-reveal'>
            <div>
              <p className='rfd-label'>Scope sheets / 02 routes</p>
              <h2 id='pricing-title' className='rfd-display'>
                Pricing &<br />
                Packages
              </h2>
            </div>
            <p>
              Choose the package that fits your needs. All packages include
              post-launch support.
            </p>
          </div>
          <div className='rfd-scope-grid rfd-reveal'>
            <article className='rfd-scope' id='brand-essentials'>
              <header className='rfd-scope-head'>
                <span className='rfd-scope-index'>SCOPE / 01</span>
                <h3>Starter</h3>
                <p className='rfd-scope-price'>$1,995-$3,995</p>
                <p className='rfd-scope-audience'>
                  Startups and MVPs on a budget
                </p>
              </header>
              <ul className='rfd-scope-list'>
                <li>Logo design (3 concepts, 2 revisions)</li>
                <li>Color palette (primary, secondary, accent)</li>
                <li>Typography system (2-3 font pairings)</li>
                <li>Basic brand guidelines (15-20 pages)</li>
                <li>Business card design</li>
                <li>Email signature template</li>
                <li>Social media profile graphics</li>
                <li>Source files (AI, Figma, PNG, SVG)</li>
              </ul>
              <footer className='rfd-scope-time'>
                <span>Estimated Timeline:</span>
                <strong>2-3 weeks</strong>
              </footer>
            </article>
            <article className='rfd-scope' id='complete-system'>
              <header className='rfd-scope-head'>
                <span className='rfd-popular'>Most Popular</span>
                <span className='rfd-scope-index'>SCOPE / 02</span>
                <h3>Growth</h3>
                <p className='rfd-scope-price'>$5,995-$9,995</p>
                <p className='rfd-scope-highlight'>
                  Only $4K more but includes complete system + templates +
                  social media + email templates
                </p>
                <p className='rfd-scope-audience'>
                  Growth companies and full rebrands
                </p>
              </header>
              <ul className='rfd-scope-list'>
                <li>Everything in Starter, plus:</li>
                <li>Comprehensive logo suite (variations)</li>
                <li>Complete color system</li>
                <li>Photography style guide</li>
                <li>Illustration style and icons</li>
                <li>Brand voice and messaging</li>
                <li>Comprehensive guidelines (40-60 pages)</li>
                <li>Marketing templates (pitch deck, one-pager)</li>
                <li>Social media templates (20+)</li>
                <li>Email marketing templates (5 layouts)</li>
                <li>Stationery suite</li>
                <li>Design system in Figma</li>
                <li>Source files + assets library</li>
              </ul>
              <footer className='rfd-scope-time'>
                <span>Estimated Timeline:</span>
                <strong>4-6 weeks</strong>
              </footer>
            </article>
          </div>

          <div className='rfd-compare rfd-reveal'>
            <div className='rfd-compare-intro'>
              <p className='rfd-label'>Agency ledger</p>
              <h3>Why Pixelmojo?</h3>
              <p>Compare traditional agencies vs our AI-powered approach</p>
            </div>
            <div className='rfd-compare-grid'>
              <div className='rfd-compare-col'>
                <div className='rfd-compare-title'>Traditional Agency</div>
                <div className='rfd-compare-row'>6-12 month timelines</div>
                <div className='rfd-compare-row'>$50K-$200K+ budgets</div>
                <div className='rfd-compare-row'>Fixed deliverables</div>
                <div className='rfd-compare-row'>Limited revisions</div>
                <div className='rfd-compare-row'>Handoff and done</div>
                <div className='rfd-compare-row'>No ongoing optimization</div>
              </div>
              <div className='rfd-compare-col'>
                <div className='rfd-compare-title'>Pixelmojo (AI-Powered)</div>
                <div className='rfd-compare-row'>4-12 week delivery</div>
                <div className='rfd-compare-row'>$1,995-$49,995 budgets</div>
                <div className='rfd-compare-row'>Flexible and iterative</div>
                <div className='rfd-compare-row'>Collaborative process</div>
                <div className='rfd-compare-row'>
                  Post-launch support included
                </div>
                <div className='rfd-compare-row'>Built for optimization</div>
              </div>
            </div>
          </div>
          <div className='rfd-call-band rfd-reveal'>
            <div className='rfd-call-main'>
              <div>
                <p className='rfd-label rfd-label-blue'>
                  Free 30-Min Strategy Call
                </p>
                <h3>Let's talk about your project</h3>
                <p>
                  We'll review your needs, discuss options, and create a custom
                  proposal. No pressure, no obligation.
                </p>
              </div>
              <a className='rfd-btn rfd-btn-yellow' href='/contact-us'>
                Book your free call{' '}
                <span className='rfd-arrow' aria-hidden>
                  →
                </span>
              </a>
            </div>
            <aside className='rfd-call-local'>
              <h4>🇵🇭 Philippines Startup Special</h4>
              <p className='rfd-discount'>15-20% off</p>
              <p>
                We're based in the Philippines and want to support local
                startups. Book a call to see if you qualify.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <section className='rfd-routes' aria-labelledby='routes-title'>
        <div className='rfd-wrap'>
          <div className='rfd-section-head rfd-reveal'>
            <div>
              <p className='rfd-label rfd-label-pink'>Two ways to start</p>
              <h2 id='routes-title' className='rfd-display'>
                Choose the
                <br />
                right route.
              </h2>
            </div>
            <p>
              Brand essentials to launch fast, or a complete system for a full
              rebrand. You own every source file.
            </p>
          </div>
          <div className='rfd-route-list rfd-reveal'>
            <a className='rfd-route' href='#brand-essentials'>
              <span className='rfd-route-index'>01</span>
              <h3>Brand Essentials</h3>
              <p>Startups and MVPs on a budget</p>
              <strong>$1,995 to $3,995</strong>
              <span className='rfd-route-time'>2 to 3 weeks</span>
              <span className='rfd-route-arrow rfd-arrow' aria-hidden>
                ↘
              </span>
            </a>
            <a className='rfd-route' href='#complete-system'>
              <span className='rfd-route-index'>02 / Most popular</span>
              <h3>Complete Brand System</h3>
              <p>Growth companies and full rebrands</p>
              <strong>$5,995 to $9,995</strong>
              <span className='rfd-route-time'>4 to 6 weeks</span>
              <span className='rfd-route-arrow rfd-arrow' aria-hidden>
                ↗
              </span>
            </a>
          </div>
        </div>
      </section>

      <section className='rfd-build' id='system' aria-labelledby='build-title'>
        <div className='rfd-wrap rfd-build-grid'>
          <div className='rfd-build-copy rfd-reveal'>
            <p className='rfd-label'>What we build</p>
            <h2 id='build-title' className='rfd-display'>
              Brand, components, and sales materials that work together.
            </h2>
            <p>
              Your logo, design system, pitch decks, and sales collateral all
              come from the same design language. No more frankenbranding across
              teams.
            </p>
            <ul className='rfd-build-points'>
              <li>
                Quantitative heuristics guide every component choice so
                experimentation teams know the expected lift.
              </li>
              <li>
                Finance, product, and marketing scorecards share the same
                metrics for a unified narrative.
              </li>
              <li>
                AI prompt libraries mirror your voice so copy, visuals, and
                video stay consistent at speed.
              </li>
            </ul>
          </div>
          <div className='rfd-registry rfd-reveal'>
            <div className='rfd-registry-head'>
              <p className='rfd-label'>Component registry</p>
              <span className='rfd-label rfd-label-blue'>Synced</span>
            </div>
            <div className='rfd-registry-title'>
              <h3>One design language</h3>
            </div>
            <div className='rfd-registry-row'>
              <span className='rfd-registry-num'>01</span>
              <strong>Logo and color system</strong>
              <span className='rfd-swatches' aria-hidden>
                <i></i>
                <i></i>
                <i></i>
              </span>
            </div>
            <div className='rfd-registry-row'>
              <span className='rfd-registry-num'>02</span>
              <strong>Typography and spacing</strong>
              <span className='rfd-label'>Aa / 08</span>
            </div>
            <div className='rfd-registry-row'>
              <span className='rfd-registry-num'>03</span>
              <strong>Voice and messaging</strong>
              <span className='rfd-label'>Tone / 01</span>
            </div>
            <div className='rfd-registry-row'>
              <span className='rfd-registry-num'>04</span>
              <strong>Components and tokens</strong>
              <span className='rfd-label'>Token / live</span>
            </div>
            <div className='rfd-registry-foot'>
              Synced from Figma to production. No frankenbrand.
            </div>
          </div>
        </div>
      </section>

      <section className='rfd-layers' aria-labelledby='layers-title'>
        <div className='rfd-wrap'>
          <div className='rfd-layers-head rfd-reveal'>
            <p className='rfd-label rfd-label-blue'>A governed stack</p>
            <h2 id='layers-title' className='rfd-display'>
              The layers of a revenue-first design system
            </h2>
            <p>
              From executive story to on-page UI, everything is orchestrated so
              every stakeholder can contribute without breaking the brand.
            </p>
          </div>
          <div className='rfd-layer-stack rfd-reveal'>
            <article className='rfd-layer'>
              <div className='rfd-layer-name'>Identity Core</div>
              <h3 className='rfd-layer-title'>
                Narrative that travels everywhere
              </h3>
              <ul className='rfd-layer-list'>
                <li>
                  Messaging architecture tied to each ICP and funnel stage.
                </li>
                <li>
                  Signature visual language with prompt libraries and usage
                  guardrails.
                </li>
                <li>
                  Voice & tone frameworks so AI tools stay on-brand by default.
                </li>
              </ul>
            </article>
            <article className='rfd-layer'>
              <div className='rfd-layer-name'>Product Experience</div>
              <h3 className='rfd-layer-title'>
                Design language mapped to outcomes
              </h3>
              <ul className='rfd-layer-list'>
                <li>
                  Component decisions driven by activation, retention, and
                  expansion metrics.
                </li>
                <li>
                  Accessibility baked into tokens, patterns, and content
                  templates.
                </li>
                <li>
                  Usage analytics connected back to component adoption for
                  continuous optimization.
                </li>
              </ul>
            </article>
            <article className='rfd-layer'>
              <div className='rfd-layer-name'>Revenue Enablement</div>
              <h3 className='rfd-layer-title'>
                Assets that close deals faster
              </h3>
              <ul className='rfd-layer-list'>
                <li>
                  Interactive demo environments and deck systems aligned to
                  buyer journeys.
                </li>
                <li>
                  Sales enablement hub with motion templates, proof points, and
                  ROI calculators.
                </li>
                <li>
                  Lifecycle content kits for marketing automation and customer
                  success workflows.
                </li>
              </ul>
            </article>
            <article className='rfd-layer'>
              <div className='rfd-layer-name'>Operations & Governance</div>
              <h3 className='rfd-layer-title'>A system teams can trust</h3>
              <ul className='rfd-layer-list'>
                <li>
                  Design ops rituals that keep Figma, code, and documentation in
                  sync automatically.
                </li>
                <li>
                  Contribution models and review gates so updates ship without
                  bottlenecks.
                </li>
                <li>
                  Change logs and rollout playbooks for cross-team visibility.
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className='rfd-adoption' aria-labelledby='adoption-title'>
        <div className='rfd-wrap rfd-adoption-grid'>
          <div className='rfd-adoption-copy rfd-reveal'>
            <p className='rfd-label'>How your team adopts it</p>
            <h2 id='adoption-title' className='rfd-display'>
              We train your team so the system actually gets used.
            </h2>
            <p>
              Design systems fail when nobody uses them. We run training
              sessions, create Loom walkthroughs, and set up governance so the
              system stays consistent after we leave.
            </p>
            <ul className='rfd-adoption-points'>
              <li>
                Executive briefings connect investment to ARR and brand equity
                goals.
              </li>
              <li>
                Team-specific playbooks give product, marketing, and success the
                assets they need on day one.
              </li>
              <li>
                Async office hours, Loom libraries, and change logs keep
                adoption high long-term.
              </li>
            </ul>
          </div>
          <div className='rfd-adoption-board rfd-reveal'>
            <div className='rfd-adoption-lane'>
              <p className='rfd-label'>Most design systems</p>
              <h3>Get built, then nobody uses them</h3>
            </div>
            <div className='rfd-adoption-lane'>
              <p className='rfd-label rfd-label-blue'>With Pixelmojo</p>
              <h3>Adoption is designed in.</h3>
              <div className='rfd-adoption-row'>
                <span>Team training and Loom walkthroughs</span>
                <span>Governance and review gates</span>
                <span>Change logs keep it consistent</span>
              </div>
              <div className='rfd-adoption-result'>
                Adoption stays high long after we roll off.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className='rfd-process'
        id='process'
        aria-labelledby='process-title'
      >
        <div className='rfd-wrap'>
          <div className='rfd-section-head rfd-reveal'>
            <div>
              <p className='rfd-label'>Audit to adoption, in weeks</p>
              <h2 id='process-title' className='rfd-display'>
                Every phase lands something real.
              </h2>
            </div>
            <p>
              Each phase lands tangible assets, approvals, and learnings your
              team can reuse.
            </p>
          </div>
          <div className='rfd-timeline rfd-reveal'>
            <article className='rfd-phase'>
              <span className='rfd-phase-dot' aria-hidden></span>
              <p className='rfd-label'>Weeks 1-2</p>
              <h3>Signal scan</h3>
              <p>
                Audit every touchpoint for conversion gaps and visual drift.
              </p>
            </article>
            <article className='rfd-phase'>
              <span className='rfd-phase-dot' aria-hidden></span>
              <p className='rfd-label'>Weeks 3-4</p>
              <h3>System blueprint</h3>
              <p>
                Narrative, component hierarchy, and asset recipes get defined.
              </p>
            </article>
            <article className='rfd-phase'>
              <span className='rfd-phase-dot' aria-hidden></span>
              <p className='rfd-label'>Weeks 5-7</p>
              <h3>Build and activation</h3>
              <p>
                Tokens, coded components, and templates ship; teams start using
                them.
              </p>
            </article>
            <article className='rfd-phase'>
              <span className='rfd-phase-dot' aria-hidden></span>
              <p className='rfd-label'>Weeks 8-9</p>
              <h3>Scale and governance</h3>
              <p>Automation and rollout plans lock in long-term adoption.</p>
            </article>
          </div>
        </div>
      </section>

      <section className='rfd-launches' aria-labelledby='launch-title'>
        <div className='rfd-wrap'>
          <p className='rfd-label rfd-label-pink'>Release manifest</p>
          <h2 id='launch-title' className='rfd-display rfd-reveal'>
            What launches
            <br />
            with your system
          </h2>
          <div className='rfd-launch-grid rfd-reveal'>
            <article className='rfd-launch-card'>
              <span className='rfd-launch-index'>01 / Measurement</span>
              <h3>Activation dashboard</h3>
              <p>
                Live analytics connect component usage, landing-page tests, and
                revenue KPIs.
              </p>
            </article>
            <article className='rfd-launch-card'>
              <span className='rfd-launch-index'>02 / Enablement</span>
              <h3>GTM enablement suite</h3>
              <p>
                Sales decks, video scripts, and case study modules generated
                straight from the system.
              </p>
            </article>
            <article className='rfd-launch-card'>
              <span className='rfd-launch-index'>03 / Governance</span>
              <h3>Brand command center</h3>
              <p>
                Centralised documentation, AI prompts, and change logs that
                scale with your team.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='rfd-faq' id='faq' aria-labelledby='faq-title'>
        <div className='rfd-wrap rfd-faq-grid'>
          <div className='rfd-faq-title rfd-reveal'>
            <p className='rfd-label'>Questions / answered</p>
            <h2 id='faq-title' className='rfd-display'>
              Frequently Asked Questions
            </h2>
            <p>Everything you need to know about working with Pixelmojo.</p>
          </div>
          <div className='rfd-faq-list rfd-reveal'>
            {faqItems.slice(0, 4).map((item, i) => (
              <details key={item.question} open={i === 0}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
            {faqItems.slice(4).map(item => (
              <details
                key={item.question}
                className='rfd-faq-more'
                id='faq-more'
                hidden={!faqOpen}
              >
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
            {faqItems.length > 4 && (
              <button
                className='rfd-show-more'
                type='button'
                aria-expanded={faqOpen}
                aria-controls='faq-more'
                onClick={() => setFaqOpen(v => !v)}
              >
                {faqOpen ? 'Show less' : `Show ${faqItems.length - 4} more`}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className='rfd-proof' aria-labelledby='proof-title'>
        <div className='rfd-wrap'>
          <div className='rfd-proof-head rfd-reveal'>
            <div>
              <p className='rfd-label'>Proof, not promises</p>
              <h2 id='proof-title' className='rfd-display'>
                The system is running where you can see it
              </h2>
            </div>
          </div>
          <div className='rfd-proof-grid rfd-reveal'>
            <article className='rfd-proof-item'>
              <span className='rfd-label'>This site</span>
              <h3>
                Our own brand and product UI run on the same design system we
                build for you.
              </h3>
              <p className='rfd-proof-tag'>You're looking at it</p>
            </article>
            <article className='rfd-proof-item'>
              <span className='rfd-label'>Source files</span>
              <h3>
                Logo, design system, and every asset ship to you as editable
                source.
              </h3>
              <p className='rfd-proof-tag'>Fully owned.</p>
            </article>
            <article className='rfd-proof-item'>
              <span className='rfd-label'>One language</span>
              <h3>
                Brand, product, and sales assets all speak the same visual
                language.
              </h3>
              <p className='rfd-proof-tag'>No frankenbrand.</p>
            </article>
          </div>
        </div>
      </section>

      <section className='rfd-final-cta' aria-labelledby='cta-title'>
        <div className='rfd-wrap rfd-cta-grid'>
          <h2 id='cta-title' className='rfd-display rfd-reveal'>
            Let's see if your brand needs this.
          </h2>
          <div className='rfd-cta-side rfd-reveal'>
            <p>
              30-minute call. We will look at your current brand, your product,
              and your sales materials. If a logo is all you need, we will say
              that upfront.
            </p>
            <a className='rfd-btn rfd-btn-dark' href='/contact-us'>
              Schedule the system audit{' '}
              <span className='rfd-arrow' aria-hidden>
                ↗
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
